// V42 Pass 4 — BSM (Beyond the Standard Model) info panel.
//
// Content from Ian's notes (elementary_particles_notes.md, 2026-05-20).
// Deliberately separate from the main Particles grid: BSM particles are
// HYPOTHESES, not confirmed physics, and showing them alongside electrons
// and quarks in the same table — even with a "hypothetical" tag — risks
// the misconception that they exist. This panel is opt-in: a button at
// the bottom of Particles.jsx opens it. Clear framing throughout that
// NONE of these particles have been observed.
//
// Single graviton row in the main grid is the exception (Pass 1/2):
// it's the only one with quantum-gravity-mandatory motivation strong
// enough to belong in the "four forces" picture. Everything else lives
// here.

import { PHYS_ACCENT } from "./PhysShell.jsx";

const SECTIONS = [
  {
    title: "Supersymmetry (SUSY)",
    blurb:
      "Every Standard Model particle has a 'superpartner' with spin differing by ½. Heavily explored for decades; the LHC has not found low-mass superpartners, which has weakened 'natural' SUSY scenarios but not killed the framework.",
    items: [
      ["squark (q̃)", "spin-0 partner of quarks"],
      ["selectron (ẽ)", "spin-0 partner of the electron"],
      ["photino (γ̃)", "spin-½ partner of the photon"],
      ["gluino (g̃)", "spin-½ partner of the gluon"],
      ["wino, zino, higgsino", "spin-½ partners of W, Z, Higgs"],
      ["gravitino", "spin-3/2 partner of the (hypothetical) graviton"],
      ["neutralino", "mix of photino/zino/higgsinos — leading dark-matter candidate (lightest SUSY particle)"],
    ],
  },
  {
    title: "Dark matter candidates",
    blurb:
      "Astronomical evidence for dark matter is overwhelming. We just don't know what it is. These are the leading proposals.",
    items: [
      ["WIMP", "generic ~100 GeV–TeV particle interacting via the weak force; direct-detection limits increasingly tight"],
      ["axion", "very light, weakly coupled; also solves the strong-CP problem"],
      ["sterile neutrino", "right-handed neutrino that doesn't feel the weak force"],
      ["primordial black holes", "not a particle; formed in the early universe — windows in mass range remain open"],
    ],
  },
  {
    title: "Grand unification (GUTs)",
    blurb:
      "Theories that unify the strong, weak, and EM forces at very high energy. Predict new heavy particles — none seen yet.",
    items: [
      ["X and Y bosons", "mediate proton decay; mass ~10¹⁵ GeV, far beyond any collider"],
      ["magnetic monopole", "net magnetic charge; predicted by most GUTs, never observed despite long searches"],
      ["leptoquark", "carries both lepton and baryon number; appears in many GUTs and some B-meson anomalies"],
    ],
  },
  {
    title: "Other commonly discussed",
    blurb:
      "Particles proposed to solve specific open problems (hidden sectors, neutrino mass, inflation, dark energy).",
    items: [
      ["dark photon (A′)", "hidden-sector U(1) boson that mixes weakly with the photon"],
      ["Z′", "additional Z-like boson from an extra U(1) symmetry"],
      ["W′", "heavier W cousin; predicted by left-right symmetric models"],
      ["majoron", "pseudo-Nambu-Goldstone boson from spontaneous lepton-number breaking"],
      ["inflaton", "scalar that drove cosmic inflation — could be the Higgs, an axion, or new"],
      ["quintessence", "dynamical scalar field as dark-energy candidate"],
      ["right-handed neutrino", "needed for Dirac neutrino masses or the see-saw mechanism"],
      ["tachyon", "hypothetical superluminal particle; almost certainly not physical"],
    ],
  },
];

const PROBLEMS = [
  ["Dark matter", "WIMP, axion, sterile neutrino, primordial BHs, neutralino"],
  ["Matter–antimatter asymmetry", "heavy Majorana neutrinos (leptogenesis), extra Higgs doublets"],
  ["Hierarchy problem (why Higgs is light)", "SUSY, composite Higgs, extra dimensions"],
  ["Neutrino mass", "right-handed neutrinos, see-saw, R-parity-violating SUSY"],
  ["Strong CP problem", "axion"],
  ["Gauge coupling unification", "SUSY, GUTs"],
  ["Quantum gravity", "graviton, string theory, loop quantum gravity"],
  ["Dark energy", "cosmological constant or quintessence"],
  ["Cosmic inflation", "inflaton — could be the Higgs, an axion, or new"],
];

export function BSMPanel({ onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, zIndex: 60,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#13141F",
          border: "1px dashed #6B7280",  // dashed to echo the hypothetical visual language from the main grid
          borderRadius: 18,
          padding: "28px 26px",
          maxWidth: 560,
          width: "100%",
          maxHeight: "88vh",
          overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#E8E9F3", margin: 0 }}>
            Beyond the Standard Model
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "rgba(255,255,255,0.05)", border: "none", color: "#9CA3AF",
              width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 17, fontFamily: "inherit",
            }}
          >✕</button>
        </div>
        <p style={{ fontSize: 12.5, color: "#FBBF24", margin: "0 0 4px", fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase" }}>
          None observed
        </p>
        <p style={{ fontSize: 13, color: "#9A9DBE", margin: "0 0 18px", lineHeight: 1.55 }}>
          The Standard Model is the most precisely-tested theory in science, but it has known
          gaps: no dark-matter candidate, no quantum gravity, no explanation for the
          matter-antimatter asymmetry. The particles below are the leading proposals to fill
          those gaps. None has been detected. This is a map of the active hypothesis space.
        </p>

        {SECTIONS.map((s) => (
          <div key={s.title} style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 14.5, fontWeight: 700, color: PHYS_ACCENT, margin: "0 0 6px" }}>{s.title}</h3>
            <p style={{ fontSize: 12.5, color: "#9A9DBE", margin: "0 0 10px", lineHeight: 1.55 }}>{s.blurb}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {s.items.map(([name, desc]) => (
                <div key={name} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 12.5 }}>
                  <span style={{ color: "#E8E9F3", fontWeight: 600, flex: "0 0 130px", lineHeight: 1.45 }}>{name}</span>
                  <span style={{ color: "#9A9DBE", lineHeight: 1.45 }}>{desc}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div style={{ marginTop: 26, paddingTop: 18, borderTop: "1px solid #1C1E2E" }}>
          <h3 style={{ fontSize: 14.5, fontWeight: 700, color: PHYS_ACCENT, margin: "0 0 12px" }}>
            Which problem does each solve?
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {PROBLEMS.map(([prob, sols]) => (
              <div key={prob} style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                <span style={{ color: "#E8E9F3", fontWeight: 600 }}>{prob}</span>
                <span style={{ color: "#5B5E7A" }}>{" → "}</span>
                <span style={{ color: "#9A9DBE" }}>{sols}</span>
              </div>
            ))}
          </div>
        </div>

        <p style={{ fontSize: 11.5, color: "#5B5E7A", margin: "22px 0 0", lineHeight: 1.55, fontStyle: "italic" }}>
          The next confirmed particle — whenever it comes — will be one of the most important
          discoveries in physics.
        </p>

        <button
          onClick={onClose}
          style={{
            marginTop: 22, width: "100%", padding: "11px", borderRadius: 10, border: "none",
            background: PHYS_ACCENT, color: "#06121C", fontWeight: 700, fontSize: 14, cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}

export default BSMPanel;
